import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

/** Escape one CSV field per RFC 4180 (quote if it has comma/quote/newline). */
function csvField(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Build a CSV string from a header row and data rows. */
export function toCsv(header: string[], rows: string[][]): string {
  const out = [header.map(csvField).join(',')];
  for (const row of rows) out.push(row.map(csvField).join(','));
  return out.join('\n');
}

/** Trigger a browser download of text content. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Render a report into a PDF: an optional captured chart image at the top,
 * then one or more titled tables.
 */
export async function exportReportPdf(
  filename: string,
  title: string,
  chartEl: HTMLElement | null,
  tables: { heading: string; head: string[]; body: string[][] }[],
): Promise<void> {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  let y = margin;

  doc.setFontSize(16);
  doc.text(title, margin, y);
  y += 20;

  if (chartEl) {
    const canvas = await html2canvas(chartEl, { scale: 2, backgroundColor: '#ffffff' });
    const img = canvas.toDataURL('image/png');
    const pageWidth = doc.internal.pageSize.getWidth() - margin * 2;
    const imgWidth = Math.min(pageWidth, 240);
    const imgHeight = (canvas.height / canvas.width) * imgWidth;
    doc.addImage(img, 'PNG', margin, y, imgWidth, imgHeight);
    y += imgHeight + 20;
  }

  for (const t of tables) {
    doc.setFontSize(12);
    doc.text(t.heading, margin, y);
    y += 8;
    autoTable(doc, { head: [t.head], body: t.body, startY: y, margin: { left: margin, right: margin } });
    // @ts-expect-error lastAutoTable is added by the plugin at runtime
    y = (doc.lastAutoTable?.finalY ?? y) + 20;
  }

  doc.save(filename);
}
